import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp, Eyebrow, usePageTitle, LineReveal } from "@/components/Motion";

const inputCls =
  "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-orange focus:ring-2 focus:ring-orange/20";

export default function Contact() {
  usePageTitle("Contact");
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Inquiry sent — talk soon!");
      setSent(true);
      setForm({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong — please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-16" data-testid="contact-page">
      <SEO
        title="Contact Incolour Films"
  description="Contact Incolour Films for film, television, commercial and branded-content production in Toronto and across Ontario."
  path="/contact"
      />
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>Contact</Eyebrow>
        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl" data-testid="contact-title">
          <LineReveal lines={["Tell us what", "you're dreaming up."]} />
        </h1>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 pb-24 sm:px-6 lg:grid-cols-12 lg:px-8">
        {/* INFO */}
        <FadeUp className="lg:col-span-5">
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src="https://images.unsplash.com/photo-1517935706615-2717063c2225?auto=format&fit=crop&w=1600&q=80"
              alt="Toronto"
              loading="lazy"
              className="aspect-[16/9] w-full object-cover"
            />
          </div>
          <div className="mt-8 space-y-6">
            <div className="flex gap-4">
              <MapPin size={20} className="mt-1 shrink-0 text-orange" />
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Studio</p>
                <p className="mt-1 text-sm leading-relaxed">Toronto &amp; Mississauga<br />Ontario, Canada</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Mail size={20} className="mt-1 shrink-0 text-orange" />
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Email</p>
                <a href="mailto:connect@incolourfilms.ca" className="mt-1 block text-sm font-semibold hover:text-orange" data-testid="contact-email-link">connect@incolourfilms.ca</a>
              </div>
            </div>
            <div className="flex gap-4">
              <Clock size={20} className="mt-1 shrink-0 text-orange" />
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Hours</p>
                <p className="mt-1 text-sm">Mon–Fri, 9am–6pm ET. Sets, as needed.</p>
              </div>
            </div>
          </div>
        </FadeUp>

        {/* FORM */}
        <FadeUp delay={0.12} className="lg:col-span-7">
          <form onSubmit={submit} className="rounded-2xl border border-border bg-cream2 p-6 dark:bg-card sm:p-10" data-testid="contact-form">
            {sent && (
              <div className="mb-6 rounded-xl border border-orange/40 bg-orange/10 px-5 py-4 text-sm" data-testid="contact-success-message">
                Thanks — your inquiry is in. We reply within two business days.
              </div>
            )}
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-name" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Name *</label>
                <input id="cf-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="cf-email" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Email *</label>
                <input id="cf-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="cf-company" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Company / Org</label>
                <input id="cf-company" value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-company-input" placeholder="Optional" />
              </div>
              <div>
                <label htmlFor="cf-type" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Project type</label>
                <select id="cf-type" value={form.project_type} onChange={set("project_type")} className={inputCls} data-testid="contact-type-select">
                  <option value="">Select…</option>
                  <option>Original Film Collaboration</option>
                  <option>Commercial / Brand</option>
                  <option>Music Video</option>
                  <option>Series / TV</option>
                  <option>Post-Production Only</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-budget" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Budget range</label>
                <select id="cf-budget" value={form.budget} onChange={set("budget")} className={inputCls} data-testid="contact-budget-select">
                  <option value="">Select…</option>
                  <option>Under $25k</option>
                  <option>$25k – $100k</option>
                  <option>$100k – $500k</option>
                  <option>$500k – $2M</option>
                  <option>$2M+</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="mb-2 block text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">About the project *</label>
                <textarea id="cf-message" required rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" placeholder="Timeline, references, where you are in the process — anything helps." />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-btn"
              className="mt-8 w-full rounded-full bg-orange px-8 py-4 text-sm font-semibold text-cream transition-transform hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
            >
              {sending ? "Sending…" : "Send inquiry"}
            </button>
          </form>
        </FadeUp>
      </section>
    </div>
  );
}
